/**
 * Diagnóstico CIE-10: lista desplegable con búsqueda. Al tocar el campo se abre la lista con los diagnósticos del paciente (si se conocen)
 * y los más usados en odontología; al escribir una palabra de la consulta («absceso», «muela del juicio», «sangrado encías») o el código («K04»)
 * se filtra y se ofrecen las opciones para elegir. Con teclado: flechas para moverse, Enter para elegir, Esc para cerrar.
 * Con `opciones` la lista se limita a esas (p. ej. los 163 diagnósticos del certificado IESS).
 */
import { useId, useState } from "react";
import { buscarCie10, CIE10, norm } from "../../../dominio/src/index.ts";

/** Los diagnósticos que más se usan en la consulta odontológica (se muestran al abrir la lista). */
const FRECUENTES = ["K02.1", "K02.0", "K02.2", "K04.0", "K04.1", "K04.4", "K04.5", "K04.6", "K04.7", "K05.1", "K05.3", "K03.6", "K03.1", "K03.2", "K03.0", "K08.1", "K08.3", "K01.1",
  "K00.6", "K07.3", "K07.6", "K12.0", "K08.8", "K06.0", "K06.1", "K10.2", "Z01.2"];

type Fila = { codigo: string; descripcion: string };

export function BuscadorCie10({ valor, alCambiar, sugeridos = [], etiqueta = "Diagnóstico (CIE-10)", obligatorio = false, opciones }: {
  valor: string; alCambiar: (codigo: string) => void; sugeridos?: readonly string[]; etiqueta?: string; obligatorio?: boolean;
  /** si se da, solo se puede elegir entre estas (código y nombre) */
  opciones?: readonly (readonly [string, string])[];
}) {
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const id = useId();
  const nombre = (c: string) => CIE10[c] ?? opciones?.find(([k]) => k === c)?.[1] ?? "";

  const lista = (): { titulo?: string; filas: Fila[] }[] => {
    const q = texto.trim();
    if (opciones) {
      const n = norm(q);
      const filas = opciones.filter(([c, d]) => !n || norm(`${c} ${d}`).includes(n)).slice(0, 60).map(([c, d]) => ({ codigo: c, descripcion: d }));
      return [{ filas }];
    }
    if (q) return [{ filas: buscarCie10(q, 12).map((r) => ({ codigo: r.codigo, descripcion: r.descripcion })) }];
    const delPaciente = sugeridos.filter((c) => CIE10[c] && c !== valor);
    const comunes = FRECUENTES.filter((c) => CIE10[c] && !delPaciente.includes(c));
    return [
      ...(delPaciente.length ? [{ titulo: "Del odontograma del paciente", filas: delPaciente.map((c) => ({ codigo: c, descripcion: CIE10[c] })) }] : []),
      { titulo: "Más usados", filas: comunes.map((c) => ({ codigo: c, descripcion: CIE10[c] })) },
    ];
  };
  const grupos = abierto ? lista() : [];
  const planas = grupos.flatMap((g) => g.filas);
  const elegir = (codigo: string) => { alCambiar(codigo); setTexto(""); setAbierto(false); setActivo(0); };

  if (valor) {
    return (
      <div className="campo">
        <span>{etiqueta}{obligatorio ? " *" : ""}</span>
        <div className="cie-elegido">
          <b className="mono">{valor}</b><span>{nombre(valor)}</span>
          <button type="button" className="btn chico" onClick={() => alCambiar("")} aria-label={`Quitar el diagnóstico ${valor}`}>Cambiar</button>
        </div>
      </div>
    );
  }
  let k = -1;
  return (
    <div className="campo buscador-cie">
      <label htmlFor={`${id}-q`}>{etiqueta}{obligatorio ? " *" : ""}</label>
      <div className="fila" style={{ gap: 4, flexWrap: "nowrap" }}>
        <input id={`${id}-q`} role="combobox" aria-expanded={abierto && planas.length > 0} aria-controls={`${id}-lista`} aria-autocomplete="list" style={{ flex: 1 }}
          aria-activedescendant={abierto && planas[activo] ? `${id}-${activo}` : undefined}
          autoComplete="off" placeholder={opciones ? "Elige de la lista o escribe para buscar…" : "Elige de la lista o escribe: caries profunda, absceso, sangrado encías… o el código"} value={texto}
          onChange={(e) => { setTexto(e.target.value); setAbierto(true); setActivo(0); }}
          onFocus={() => setAbierto(true)} onBlur={() => setTimeout(() => setAbierto(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setAbierto(true); setActivo((a) => Math.min(a + 1, planas.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setActivo((a) => Math.max(a - 1, 0)); }
            else if (e.key === "Enter" && planas[activo]) { e.preventDefault(); elegir(planas[activo].codigo); }
            else if (e.key === "Escape") setAbierto(false);
          }} />
        <button type="button" className="btn chico" aria-label="Ver la lista de diagnósticos" aria-expanded={abierto} onMouseDown={(e) => { e.preventDefault(); setAbierto(!abierto); }}>▾</button>
      </div>
      {abierto && (
        <ul className="cie-lista" id={`${id}-lista`} role="listbox" style={{ maxHeight: 320, overflowY: "auto" }}>
          {grupos.map((g, gi) => (
            <li key={gi} role="presentation" style={{ padding: 0, cursor: "default" }}>
              {g.titulo && <div className="muted" style={{ fontSize: 12, padding: "6px 10px 2px" }}>{g.titulo}</div>}
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {g.filas.map((r) => { k += 1; const n = k; return (
                  <li key={r.codigo} id={`${id}-${n}`} role="option" aria-selected={n === activo}
                    onMouseDown={(e) => { e.preventDefault(); elegir(r.codigo); }} onMouseEnter={() => setActivo(n)}>
                    <b className="mono">{r.codigo}</b> {r.descripcion}
                  </li>
                ); })}
              </ul>
            </li>
          ))}
          {!planas.length && <li className="muted" role="presentation">Sin coincidencias. Prueba con otra palabra o con el código.</li>}
        </ul>
      )}
    </div>
  );
}
